import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Trophy, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MethodTick, PrepMethodDialog } from "@/components/ops/PrepMethodTab";

/**
 * Phase 3 — Item-wise net profit engine.
 *   • Manager enters units_sold per item for the month.
 *   • Backend joins with recipe costing + month's expenses to compute
 *     per-item revenue, COGS, gross, allocated overhead and net profit.
 */
export default function ItemProfit() {
  const today = new Date().toISOString().slice(0, 7);
  const [me, setMe] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [month, setMonth] = useState(today);
  const [cats, setCats] = useState([]);
  const [itemsByCat, setItemsByCat] = useState({});
  const [selectedCat, setSelectedCat] = useState("");
  const [sales, setSales] = useState([]);        // item_sales for this outlet/month
  const [profit, setProfit] = useState(null);    // per-item profit payload
  const [companyRanking, setCompanyRanking] = useState(null);
  const [busy, setBusy] = useState({});
  const [drafts, setDrafts] = useState({});      // { [item_id]: units }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [methodItem, setMethodItem] = useState(null);
  const isAdmin = me?.role === "admin";
  const canEditMethod = isAdmin || me?.permissions?.prep_edit === true;
  const onMethodSaved = (id, text) => setItemsByCat(m => Object.fromEntries(Object.entries(m).map(([k, arr]) => [k, arr.map(x => x.id === id ? { ...x, method: text } : x)])));

  useEffect(() => {
    Promise.all([
      api.get("/auth/me").then(r => { setMe(r.data); if (r.data.role !== "admin") setOutletId(r.data.outlet_id || ""); }),
      api.get("/ops/outlets").then(r => setOutlets(r.data)),
      api.get("/categories").then(r => { setCats(r.data); if (r.data[0]) setSelectedCat(r.data[0].id); }),
    ]).catch(() => {});
  }, []);

  // Load items for the selected category
  useEffect(() => {
    if (!selectedCat) return;
    if (itemsByCat[selectedCat]) return;
    api.get(`/items?category_id=${selectedCat}`).then(r => setItemsByCat(s => ({ ...s, [selectedCat]: r.data }))).catch(() => {});
  }, [selectedCat]);

  const reload = async () => {
    if (isAdmin && !outletId) { setSales([]); setProfit(null); return; }
    try {
      const params = new URLSearchParams({ month });
      if (isAdmin) params.set("outlet_id", outletId);
      const [s, p] = await Promise.all([
        api.get(`/ops/item-sales?${params}`).then(r => r.data),
        api.get(`/ops/item-profit?${params}`).then(r => r.data),
      ]);
      setSales(s);
      setProfit(p);
      if (isAdmin) {
        api.get(`/ops/item-profit/company?month=${month}`).then(r => setCompanyRanking(r.data)).catch(() => {});
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { if (isAdmin ? outletId : me) reload(); }, [outletId, month, me]);

  const salesById = useMemo(() => {
    const m = {}; for (const s of sales) m[s.item_id] = s; return m;
  }, [sales]);
  const profitById = useMemo(() => {
    const m = {}; for (const p of (profit?.items || [])) m[p.item_id] = p; return m;
  }, [profit]);

  const saveItem = async (itemId) => {
    const units = parseFloat(drafts[itemId]);
    if (isNaN(units) || units < 0) return toast.error("Enter units ≥ 0");
    if (isAdmin && !outletId) return toast.error("Pick an outlet first");
    setBusy(b => ({ ...b, [itemId]: true }));
    try {
      await api.post("/ops/item-sales", { outlet_id: outletId || null, month, item_id: itemId, units_sold: units });
      toast.success("Saved");
      setDrafts(d => { const c = { ...d }; delete c[itemId]; return c; });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(b => ({ ...b, [itemId]: false })); }
  };
  const clearItem = async (rid) => {
    if (!window.confirm("Clear units sold for this item?")) return;
    try { await api.delete(`/ops/item-sales/${rid}`); toast.success("Cleared"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const items = itemsByCat[selectedCat] || [];
  const outletName = outlets.find(o => o.id === (isAdmin ? outletId : me?.outlet_id))?.name;

  return (
    <div className="space-y-5" data-testid="item-profit-root">
      <PrepMethodDialog item={methodItem} canEdit={canEditMethod} email={me?.email} onClose={() => setMethodItem(null)} onSaved={onMethodSaved} />
      {/* Controls */}
      <div className="rounded-2xl p-4 border flex flex-wrap gap-3 items-end" style={{ background: "#FFFDF6", borderColor: "#E4D6A8" }}>
        {isAdmin ? (
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <select data-testid="ip-outlet" value={outletId} onChange={ev => setOutletId(ev.target.value)} className="h-9 px-2 rounded border bg-white min-w-[200px]" style={{borderColor:"#E4D6A8"}}>
              <option value="">— pick outlet —</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <div className="h-9 px-3 flex items-center rounded border bg-[#FFF8E7] font-medium" style={{borderColor:"#E4D6A8"}}>{outletName || "—"}</div>
          </div>
        )}
        <div>
          <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Month</label>
          <input data-testid="ip-month" type="month" value={month} onChange={ev => setMonth(ev.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
        </div>
        <Button size="sm" data-testid="ip-bulk-open" onClick={() => setBulkOpen(true)}
          disabled={isAdmin && !outletId}
          className="h-9 rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] disabled:opacity-60">
          <Upload size={14} className="mr-1" /> Paste from POS
        </Button>
        <div className="ml-auto">
          {profit && (
            <div className="grid grid-cols-4 gap-3 text-sm">
              <T label="Revenue"      value={profit.totals.revenue} />
              <T label="COGS"         value={profit.totals.cogs} tone="neg" />
              <T label="Overhead"     value={profit.totals.allocated_overhead_total} tone="neg" />
              <T label="Net profit"   value={profit.totals.net_profit} tone={profit.totals.net_profit >= 0 ? "pos" : "neg"} big />
            </div>
          )}
        </div>
      </div>

      <BulkPasteDialog open={bulkOpen} onOpenChange={setBulkOpen}
        outletId={outletId} isAdmin={isAdmin} month={month} onSaved={reload} />

      {(isAdmin && !outletId) ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{borderColor:"#E4D6A8", color:"#7a6349"}}>Pick an outlet to key in units and see per-item profit.</div>
      ) : (
        <>
          {/* Category chooser + input grid */}
          <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <h3 className="font-display text-lg" style={{color:"#2b1a0d"}}>Enter units sold · {month}</h3>
                <div className="text-xs" style={{color:"#7a6349"}}>Type units → hit Save. Repeat for every item you sold this month.</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {cats.map(c => (
                  <button key={c.id} onClick={() => setSelectedCat(c.id)}
                    data-testid={`ip-cat-${c.id}`}
                    className={`px-3 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest border ${selectedCat === c.id ? "bg-[#3B2519] text-[#F5C242] border-transparent" : "bg-white text-[#3B2519]"}`}
                    style={{borderColor: selectedCat === c.id ? "transparent" : "#E4D6A8"}}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                    <th className="text-left py-2">Item</th>
                    <th className="text-right py-2 w-24">SP ₹</th>
                    <th className="text-right py-2 w-24">Cost ₹</th>
                    <th className="text-right py-2 w-24">Units sold</th>
                    <th className="text-right py-2 w-28">Revenue ₹</th>
                    <th className="text-right py-2 w-28">Net ₹</th>
                    <th className="py-2 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const s = salesById[it.id];
                    const p = profitById[it.id];
                    const b = !!busy[it.id];
                    const draft = drafts[it.id];
                    return (
                      <tr key={it.id} className="border-t" style={{borderColor:"#F3E7C4"}}>
                        <td className="py-2 pr-3 font-medium" style={{color:"#2b1a0d"}}>{it.name}
                          <MethodTick has={!!(it.method || "").trim()} onClick={() => setMethodItem(it)} testId={`ip-method-${it.id}`} />
                        </td>
                        <td className="py-2 pr-2 text-right">₹{Number(it.sp || 0).toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right" style={{color:"#7a6349"}}>{it.making_cost != null ? `₹${Number(it.making_cost).toFixed(2)}` : "—"}</td>
                        <td className="py-2 pr-2">
                          <input data-testid={`ip-units-${it.id}`} type="number" step="1" min="0"
                            value={draft !== undefined ? draft : (s ? s.units_sold : "")}
                            onChange={e => setDrafts(d => ({ ...d, [it.id]: e.target.value }))}
                            className="w-full h-8 px-2 text-right rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
                        </td>
                        <td className="py-2 pr-2 text-right">{p ? `₹${p.revenue.toLocaleString()}` : "—"}</td>
                        <td className="py-2 pr-2 text-right font-bold" style={{color: p ? (p.net_profit >= 0 ? "#0a7a3b" : "#c0392b") : "#7a6349"}}>{p ? `₹${p.net_profit.toLocaleString()}` : "—"}</td>
                        <td className="py-2 pl-2 flex items-center gap-1 justify-end">
                          <Button size="sm" data-testid={`ip-save-${it.id}`} disabled={b} onClick={() => saveItem(it.id)}
                            className="h-8 rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60 text-xs">
                            {b ? "…" : "Save"}
                          </Button>
                          {s && <button data-testid={`ip-clear-${it.id}`} onClick={() => clearItem(s.id)} className="text-xs text-red-500 px-2">✕</button>}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-xs" style={{color:"#7a6349"}}>Loading items…</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-item profit ranking */}
          {profit && profit.items.length > 0 && (
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-display text-lg" style={{color:"#2b1a0d"}}>Ranked by net profit · {profit.outlet.name}</h3>
                  <div className="text-xs" style={{color:"#7a6349"}}>{profit.items.length} items · overhead ₹{profit.totals.allocated_overhead_total.toLocaleString()} allocated by revenue share</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                      <th className="text-left py-2">#</th>
                      <th className="text-left py-2">Item</th>
                      <th className="text-right py-2">Units</th>
                      <th className="text-right py-2">Revenue</th>
                      <th className="text-right py-2">Gross</th>
                      <th className="text-right py-2">Overhead</th>
                      <th className="text-right py-2">Net</th>
                      <th className="text-right py-2">Net %</th>
                      <th className="text-right py-2">Tag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profit.items.map((r, i) => (
                      <tr key={r.item_id} className="border-t" style={{borderColor:"#F3E7C4"}} data-testid={`ip-row-${r.item_id}`}>
                        <td className="py-2 pr-2 font-mono2 text-xs">{i + 1 === 1 && r.net_profit > 0 ? <Trophy size={12} className="inline mr-1" style={{color:"#B4841F"}} /> : null}{i + 1}</td>
                        <td className="py-2 pr-2 font-medium" style={{color:"#2b1a0d"}}>{r.item_name}</td>
                        <td className="py-2 pr-2 text-right">{r.units_sold}</td>
                        <td className="py-2 pr-2 text-right">₹{r.revenue.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right" style={{color:"#0a7a3b"}}>₹{r.gross_profit.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right" style={{color:"#c0392b"}}>₹{r.allocated_overhead.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right font-bold" style={{color: r.net_profit >= 0 ? "#0a7a3b" : "#c0392b"}}>₹{r.net_profit.toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right"><span className={`px-2 py-0.5 rounded-full text-xs ${r.net_margin_pct >= 15 ? "bg-emerald-100 text-emerald-700" : r.net_margin_pct >= 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{r.net_margin_pct}%</span></td>
                        <td className="py-2 pl-2 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono2 uppercase tracking-widest ${
                            r.classification === "STAR"    ? "bg-yellow-100 text-yellow-800" :
                            r.classification === "PROMOTE" ? "bg-blue-100 text-blue-700" :
                            r.classification === "REPRICE" ? "bg-orange-100 text-orange-700" :
                                                             "bg-red-100 text-red-700"
                          }`}>
                            {r.classification}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[10px] font-mono2 uppercase tracking-widest flex flex-wrap gap-3" style={{color:"#7a6349"}}>
                <span><Sparkles size={10} className="inline mr-1" style={{color:"#B4841F"}} /> STAR: high volume + high margin</span>
                <span>PROMOTE: high margin, low volume</span>
                <span>REPRICE: high volume, low margin</span>
                <span>REVIEW: low volume, low margin</span>
              </div>
            </div>
          )}

          {/* Company-wide item ranking (admin only) */}
          {isAdmin && companyRanking && companyRanking.items.length > 0 && (
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }} data-testid="ip-company-root">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase" style={{color:"#B4841F"}}>Company · all outlets</div>
                  <h3 className="font-display text-xl" style={{color:"#2b1a0d"}}>Item net profit · rolled up</h3>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Company net profit</div>
                  <b style={{color: companyRanking.totals.net_profit >= 0 ? "#0a7a3b" : "#c0392b"}}>₹{companyRanking.totals.net_profit.toLocaleString()}</b>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="sticky top-0" style={{background:"#FFF8E7"}}>
                    <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                      <th className="text-left py-2">#</th>
                      <th className="text-left py-2">Item</th>
                      <th className="text-right py-2">Units</th>
                      <th className="text-right py-2">Revenue</th>
                      <th className="text-right py-2">Net</th>
                      <th className="text-right py-2">Net %</th>
                      <th className="text-right py-2">Outlets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyRanking.items.map(r => (
                      <tr key={r.item_id} className="border-t" style={{borderColor:"#F3E7C4"}}>
                        <td className="py-1.5 pr-2 font-mono2 text-xs">{r.rank}</td>
                        <td className="py-1.5 pr-2 font-medium" style={{color:"#2b1a0d"}}>{r.item_name}</td>
                        <td className="py-1.5 pr-2 text-right">{r.units_sold}</td>
                        <td className="py-1.5 pr-2 text-right">₹{r.revenue.toLocaleString()}</td>
                        <td className="py-1.5 pr-2 text-right font-bold" style={{color: r.net_profit >= 0 ? "#0a7a3b" : "#c0392b"}}>₹{r.net_profit.toLocaleString()}</td>
                        <td className="py-1.5 pr-2 text-right">{r.net_margin_pct}%</td>
                        <td className="py-1.5 pr-2 text-right">{r.outlets_present}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function T({ label, value, tone, big }) {
  const color = tone === "pos" ? "#0a7a3b" : tone === "neg" ? "#c0392b" : "#2b1a0d";
  return (
    <div>
      <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>{label}</div>
      <div className={big ? "font-display text-xl" : "font-medium"} style={{color}}>₹{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bulk paste from Petpooja / any POS item-sales report
function BulkPasteDialog({ open, onOpenChange, outletId, isAdmin, month, onSaved }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // Parse rows from pasted text. Accepts tab, comma, semicolon or 2+ spaces as separator.
  // Each line must have at least a name and a numeric quantity.
  const parseRows = () => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const parts = line.split(/\t|;|,|\s{2,}/).map(p => p.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      // Find the last numeric-looking chunk as units, everything before it as the name
      let unitsIdx = -1;
      for (let i = parts.length - 1; i >= 0; i--) {
        const n = parseFloat(parts[i].replace(/,/g, ""));
        if (!isNaN(n)) { unitsIdx = i; break; }
      }
      if (unitsIdx <= 0) continue;
      const name = parts.slice(0, unitsIdx).join(" ").replace(/^"|"$/g, "");
      const units = parseFloat(parts[unitsIdx].replace(/,/g, ""));
      // Skip header rows
      if (/^(item|name|product|dish)/i.test(name) && isNaN(units)) continue;
      if (isNaN(units) || units < 0) continue;
      rows.push({ name, units });
    }
    return rows;
  };

  const runPreview = async () => {
    const rows = parseRows();
    if (rows.length === 0) return toast.error("Nothing parsable — one line per item with a number");
    if (isAdmin && !outletId) return toast.error("Pick an outlet first");
    setBusy(true);
    try {
      const r = await api.post("/ops/item-sales/bulk", {
        outlet_id: outletId || null, month, rows, dry_run: true,
      });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const confirmSave = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post("/ops/item-sales/bulk", {
        outlet_id: outletId || null, month,
        rows: preview.matched.map(m => ({ name: m.input_name, units: m.units })),
        dry_run: false,
      });
      toast.success(`Imported ${r.data.saved} items · skipped ${r.data.skipped}`);
      setText(""); setPreview(null); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setText(""); setPreview(null); } }}>
      <DialogContent className="max-w-2xl bg-[#FFFDF6] border-[#E4D6A8]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Paste from Petpooja / POS · {month}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs" style={{color:"#7a6349"}}>
            Paste the Petpooja &ldquo;Item Sales&rdquo; report as-is. One item per line, tab / comma / semicolon separated. We&apos;ll fuzzy-match each row to your menu and show you the preview before saving.
          </div>
          <Textarea data-testid="ip-bulk-textarea" value={text} onChange={e => setText(e.target.value)}
            rows={10} className="bg-white font-mono2 text-xs"
            placeholder={"Item Name\tQty\nMasala Chai\t45\nBun Maska\t32\nCold Coffee\t18"} />
          {!preview ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="ip-bulk-preview" onClick={runPreview} disabled={busy || !text.trim()}
                className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60">
                {busy ? "Matching…" : "Preview matches"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border bg-white p-3 max-h-72 overflow-y-auto" style={{borderColor:"#E4D6A8"}}>
                <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{color:"#7a6349"}}>
                  Matched ({preview.matched.length}) · Unmatched ({preview.unmatched.length})
                </div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                    <tr><th className="text-left py-1">Pasted</th><th className="text-left py-1">Matched item</th><th className="text-right py-1">Units</th><th className="text-right py-1">Match</th></tr>
                  </thead>
                  <tbody>
                    {preview.matched.map((m, i) => (
                      <tr key={`m${i}`} className="border-t" style={{borderColor:"#F3E7C4"}} data-testid={`ip-bulk-match-${i}`}>
                        <td className="py-1 pr-2" style={{color:"#5b4530"}}>{m.input_name}</td>
                        <td className="py-1 pr-2 font-medium" style={{color:"#2b1a0d"}}>{m.matched_name}</td>
                        <td className="py-1 pr-2 text-right">{m.units}</td>
                        <td className="py-1 pl-2 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] ${m.confidence === "exact" ? "bg-emerald-100 text-emerald-700" : m.confidence === "substring" ? "bg-amber-100 text-amber-700" : "bg-orange-100 text-orange-700"}`}>{m.confidence}</span>
                        </td>
                      </tr>
                    ))}
                    {preview.unmatched.map((u, i) => (
                      <tr key={`u${i}`} className="border-t" style={{borderColor:"#F3E7C4"}} data-testid={`ip-bulk-unmatch-${i}`}>
                        <td className="py-1 pr-2" style={{color:"#c0392b"}}>{u.input_name}</td>
                        <td className="py-1 pr-2 italic text-red-500">not found in menu</td>
                        <td className="py-1 pr-2 text-right">{u.units}</td>
                        <td className="py-1 pl-2 text-right"><span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px]">skip</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setPreview(null)}>Back</Button>
                <Button data-testid="ip-bulk-confirm" onClick={confirmSave} disabled={busy || preview.matched.length === 0}
                  className="rounded-full bg-[#0a7a3b] text-white hover:bg-[#0e8b46] disabled:opacity-60">
                  {busy ? "Saving…" : `Save ${preview.matched.length} items`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

